/**
 * 태블릿 자가등록 — 승인 대기 목록 (2026-09-19)
 *
 * 학생이 매장 태블릿에서 직접 등록하면 '예비' 상태로 접수된다.
 * 원장이 여기서 확인하고 승인해야 정회원이 된다.
 *
 * 🔴 승인 전까지 그 학생은 출결·벌점·학부모 알림 대상이 **아니다**.
 *    (서버에서 is_active=false 로 두어 판정 엔진이 제외한다)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../../infrastructure/api';

interface ConsentRow {
  type: string;
  agreed: boolean;
  agreed_at: string | null;
  version: string;
}

interface PendingItem {
  id: string;
  name: string;
  phone: string;
  birth_date: string | null;
  school: string | null;
  grade: string | null;
  gender: string | null;
  parent_phone_1: string | null;
  parent1_name: string | null;
  user_number: string;
  created_at: string | null;
  consents: ConsentRow[];
}

const CONSENT_LABEL: Record<string, string> = {
  privacy_collect: '개인정보 수집·이용',
  biometric: '얼굴 정보(선택)',
  marketing: '광고 수신(선택)',
};

const fmtPhone = (p?: string | null) => {
  const d = (p || '').replace(/[^0-9]/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  return p || '-';
};

const fmtWhen = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function SelfRegistrationPending({ onChanged }: { onChanged?: () => void }) {
  const [items, setItems] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // 🔴 window.confirm 은 환경에 따라 차단되어 "눌러도 반응 없음"이 된다 (2026-07-29 제보).
  //    인라인 2단계 버튼으로 처리한다.
  const [confirmReject, setConfirmReject] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<{ count: number; items: PendingItem[] }>(
        '/api/v1/self-registration/pending'
      );
      setItems(res.data.items || []);
    } catch (e) {
      console.error('[자가등록] 목록 조회 실패:', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id: string, kind: 'approve' | 'reject') => {
    setBusyId(id);
    setMsg(null);
    try {
      const res = await apiClient.post<{ success: boolean; message: string }>(
        `/api/v1/self-registration/${id}/${kind}`, {}
      );
      setMsg(res.data.message);
      setConfirmReject(null);
      await load();
      onChanged?.();
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || '처리 중 문제가 발생했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && items.length === 0) return null;   // 대기자가 없으면 아예 안 보인다

  return (
    <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-center justify-between">
        <div className="font-bold text-amber-900">
          ⏳ 태블릿으로 등록 신청한 학생 {items.length}명 — 확인이 필요합니다
        </div>
        <button onClick={load} className="text-sm text-amber-800 underline">새로고침</button>
      </div>
      <div className="mt-1 text-xs text-amber-800">
        승인하기 전까지 이 학생들은 출결·벌점·학부모 알림 대상이 아닙니다.
      </div>

      {msg && <div className="mt-2 text-sm text-amber-900 bg-white rounded px-3 py-2">{msg}</div>}

      <div className="mt-3 space-y-2">
        {items.map((it) => (
          <div key={it.id} className="bg-white rounded-lg border border-amber-200">
            <div className="flex items-center gap-3 p-3">
              <button
                className="flex-1 text-left"
                onClick={() => setOpenId(openId === it.id ? null : it.id)}
              >
                <div className="font-semibold text-gray-900">
                  {it.name}
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    {fmtPhone(it.phone)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {[it.school, it.grade].filter(Boolean).join(' ') || '학교·학년 미입력'}
                  {' · '}{fmtWhen(it.created_at)} 신청
                </div>
              </button>

              {confirmReject === it.id ? (
                <div className="flex gap-1">
                  <button
                    disabled={busyId === it.id}
                    onClick={() => act(it.id, 'reject')}
                    className="px-3 py-1.5 rounded bg-red-600 text-white text-sm disabled:opacity-50"
                  >정말 반려</button>
                  <button
                    onClick={() => setConfirmReject(null)}
                    className="px-3 py-1.5 rounded border text-sm"
                  >취소</button>
                </div>
              ) : (
                <div className="flex gap-1">
                  <button
                    disabled={busyId === it.id}
                    onClick={() => act(it.id, 'approve')}
                    className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm disabled:opacity-50"
                  >승인</button>
                  <button
                    disabled={busyId === it.id}
                    onClick={() => setConfirmReject(it.id)}
                    className="px-3 py-1.5 rounded border border-gray-300 text-sm"
                  >반려</button>
                </div>
              )}
            </div>

            {openId === it.id && (
              <div className="border-t border-amber-100 px-3 py-2 text-sm text-gray-700 space-y-1">
                <div>생년월일: {it.birth_date || <span className="text-red-600">미입력</span>}</div>
                <div>성별: {it.gender || '-'}</div>
                <div>
                  보호자: {it.parent1_name || '-'} / {fmtPhone(it.parent_phone_1)}
                  {!it.parent_phone_1 && (
                    <span className="ml-1 text-red-600">— 번호가 없으면 학부모 알림이 안 갑니다</span>
                  )}
                </div>
                <div>회원번호: {it.user_number}</div>
                <div className="pt-1">
                  <span className="text-gray-500">동의 내역</span>
                  <ul className="mt-0.5 space-y-0.5">
                    {it.consents.map((c) => (
                      <li key={c.type}>
                        {c.agreed ? '✅' : '⬜'} {CONSENT_LABEL[c.type] || c.type}
                        <span className="ml-1 text-xs text-gray-400">
                          ({c.version} · {fmtWhen(c.agreed_at)})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
